import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHero from '../../components/feature/PageHero';

const NAV_LINKS = [
  { href: '/', icon: 'ri-home-line', label: 'Home' },
  { href: '/march-madness', icon: 'ri-basketball-line', label: 'March Madness' },
  { href: '/super-bowl-props', icon: 'ri-football-line', label: 'Super Bowl' },
  { href: '/markets', icon: 'ri-bar-chart-2-line', label: 'Markets' },
  { href: '/why-rtj', icon: 'ri-question-line', label: 'Why L2L' },
  { href: '/my-account', icon: 'ri-user-line', label: 'My Account' },
];

const COMPARISON_ROWS = [
  { feature: 'Easy to understand prices', rtj: true, rtjNote: '$1 pay $x', others: false, sportsbook: 'warn', sbNote: 'Somewhat confusing' },
  { feature: 'Parlays', rtj: true, others: false, sportsbook: 'warn', sbNote: 'Unfavorable odds' },
  { feature: 'Spread bets', rtj: true, others: 'warn', othersNote: 'Limited', sportsbook: 'warn', sbNote: 'Yes, with 10% vig' },
  { feature: 'Prop Bets', rtj: true, others: 'warn', othersNote: 'Limited', sportsbook: 'warn', sbNote: 'Uncompetitive odds' },
  { feature: 'Easy to Pick a side', rtj: true, others: false, othersNote: 'confusing yes/no', sportsbook: true },
  { feature: 'Customizable Spread', rtj: true, others: 'warn', othersNote: 'Limited', sportsbook: true },
];

const FEATURES = [
  {
    icon: 'ri-exchange-line',
    color: 'text-teal-400',
    bg: 'bg-teal-500/10',
    title: 'Exchange-Based Trading',
    desc: 'Trade shares like stocks. Buy low, sell high, or hold until settlement. No traditional bookmaker margins eating into your returns.',
  },
  {
    icon: 'ri-percent-line',
    color: 'text-emerald-400',
    bg: 'bg-emerald-500/10',
    title: 'Lower Juice',
    desc: 'Our exchange model means tighter spreads and better prices. Keep more of your winnings with industry-leading low fees.',
  },
  {
    icon: 'ri-eye-line',
    color: 'text-cyan-400',
    bg: 'bg-cyan-500/10',
    title: 'Fair & Transparent',
    desc: 'All prices are set by the market, not us. See real-time volume, price history, and market depth on every bet.',
  },
  {
    icon: 'ri-shield-check-line',
    color: 'text-amber-400',
    bg: 'bg-amber-500/10',
    title: 'CFTC Regulated',
    desc: 'Fully licensed and regulated. Your funds are held in segregated accounts with bank-level security.',
  },
  {
    icon: 'ri-trophy-line',
    color: 'text-rose-400',
    bg: 'bg-rose-500/10',
    title: 'No Bet Limits',
    desc: 'Unlike traditional sportsbooks, we welcome winning players. Trade as much as the market can handle.',
  },
  {
    icon: 'ri-line-chart-line',
    color: 'text-lime-400',
    bg: 'bg-lime-500/10',
    title: 'Live Pricing',
    desc: 'Prices update in real-time based on market activity. Get in and out of positions whenever you want.',
  },
];

const STEPS = [
  { num: '1', icon: 'ri-search-line', label: 'Browse Markets', desc: 'Find games, props, and futures you want to trade' },
  { num: '2', icon: 'ri-shopping-cart-line', label: 'Buy Shares', desc: 'Purchase shares at market price or set your own limit' },
  { num: '3', icon: 'ri-swap-line', label: 'Trade or Hold', desc: 'Sell anytime for profit or hold until settlement' },
  { num: '4', icon: 'ri-money-dollar-circle-line', label: 'Get Paid', desc: 'Winning shares pay out $1.00 each at settlement' },
];

const TESTIMONIALS = [
  {
    name: 'Michael R.',
    role: 'Professional Trader',
    avatar: 'https://public.readdy.ai/ai/img_res/2a88c51b1b9ad0e305844869b7376305.jpg',
    quote: '"Finally, a platform that treats sports betting like the financial market it is. The exchange model is a game-changer."',
  },
  {
    name: 'Sarah K.',
    role: 'Sports Analyst',
    avatar: 'https://readdy.ai/api/search-image?query=female%20sports%20analyst%20headshot%2C%20professional%20smile%2C%20casual%20attire%2C%20clean%20background%2C%20realistic%20portrait%20photography%2C%20sharp%20focus%2C%20natural%20lighting&width=80&height=80&seq=why-rtj-avatar-002&orientation=squarish',
    quote: '"The transparency and fair pricing make L2L my go-to platform. No more getting limited for winning."',
  },
  {
    name: 'David L.',
    role: 'Casual Bettor',
    avatar: 'https://readdy.ai/api/search-image?query=casual%20male%20sports%20fan%20headshot%2C%20friendly%20smile%2C%20relaxed%20attire%2C%20neutral%20background%2C%20realistic%20portrait%20photography%2C%20natural%20lighting&width=80&height=80&seq=why-rtj-avatar-003&orientation=squarish',
    quote: '"Even as a casual bettor, I appreciate the better odds and the ability to cash out whenever I want."',
  },
];

function CheckIcon() {
  return (
    <div className="w-7 h-7 flex items-center justify-center bg-teal-500/20 rounded-full">
      <i className="ri-check-line text-teal-400 text-sm"></i>
    </div>
  );
}

function CrossIcon({ note }: { note?: string }) {
  return (
    <div className="flex items-center gap-2">
      <div className="w-7 h-7 flex items-center justify-center bg-red-500/20 rounded-full">
        <i className="ri-close-line text-red-400 text-sm"></i>
      </div>
      {note && <span className="text-xs text-red-400">{note}</span>}
    </div>
  );
}

function WarnIcon({ note }: { note?: string }) {
  return (
    <div className="flex items-center gap-2">
      <div className="w-7 h-7 flex items-center justify-center bg-amber-500/20 rounded-full">
        <i className="ri-alert-line text-amber-400 text-sm"></i>
      </div>
      {note && <span className="text-xs text-amber-400">{note}</span>}
    </div>
  );
}

export default function WhyRTJPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const [marketsDropdownOpen, setMarketsDropdownOpen] = useState(false);
  const marketsDropdownRef = useRef<HTMLDivElement>(null);

  const handleMarketsMouseLeave = () => setMarketsDropdownOpen(false);

  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    const schemaId = 'schema-why-rtj';
    const existing = document.getElementById(schemaId);
    if (existing) existing.remove();
    const script = document.createElement('script');
    script.id = schemaId;
    script.type = 'application/ld+json';
    script.text = JSON.stringify([
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: 'Why L2L \u2014 Sports Trading Exchange',
        description: 'Learn why L2L is the best sports prediction market exchange. CFTC regulated, zero house edge, peer-to-peer pricing, no bet limits.',
        url: `${siteUrl}/why-rtj`,
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: 'How is L2L different from a sportsbook?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'L2L is a peer-to-peer prediction market exchange. Instead of betting against the house, you trade shares with other users. Prices are set by market demand, not a bookmaker\'s margin, resulting in fairer odds and no bet limits.',
            },
          },
          {
            '@type': 'Question',
            name: 'What is the exchange model?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Instead of betting against the house, you buy and sell shares on game outcomes. Each winning share pays $1.00 at settlement. You can also sell your position early to lock in profits or cut losses.',
            },
          },
          {
            '@type': 'Question',
            name: 'Is L2L regulated?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Yes, L2L operates under a CFTC federal license \u2014 the same regulatory framework as the Chicago Mercantile Exchange. Your funds are held in segregated, insured accounts.',
            },
          },
          {
            '@type': 'Question',
            name: 'Are there bet limits on L2L?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'No. Unlike traditional sportsbooks that limit or ban winning players, L2L welcomes sharp bettors. Winning players improve market efficiency and provide liquidity for others.',
            },
          },
        ],
      },
    ]);
    document.head.appendChild(script);
    return () => {
      const el = document.getElementById(schemaId);
      if (el) el.remove();
    };
  }, []);

  return (
    <div className="min-h-screen" style={{ background: '#2b2b2b' }}>
      {/* Demo Banner */}
      <div className="bg-amber-500 text-slate-900 px-4 py-2 z-[100] shadow-md">
        <div className="max-w-7xl mx-auto flex items-center justify-center gap-2 text-center">
          <i className="ri-alert-line text-lg"></i>
          <span className="font-bold text-sm md:text-base tracking-wide uppercase">
            Demo Only — Experience the Future of Sports Betting — From Sportsbook to Prediction Market
          </span>
        </div>
      </div>

      {/* Navigation */}
      <nav className="sticky top-0 z-50 backdrop-blur-lg border-b border-slate-700/50" style={{ background: 'rgba(43,43,43,0.97)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-3 gap-4">
            <a className="flex items-center gap-2 flex-shrink-0" href="/">
              <div className="h-11 w-44 flex items-center justify-center">
                <img
                  src="https://static.readdy.ai/image/bc21bbd943df20a73e2c62e0eb37a79f/356d7ca1b6cdb5f780c70d0d0bd7c02d.png"
                  alt="RTJ Logo"
                  className="h-full w-full object-contain"
                />
              </div>
            </a>
            <div className="hidden lg:flex items-center justify-center gap-1 flex-1">
              <a href="/">
                <div className="px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap flex items-center gap-2 text-slate-300 hover:bg-slate-800/50 hover:text-white">
                  <i className="ri-home-line w-4 h-4 flex items-center justify-center"></i>
                  Home
                </div>
              </a>
              <a href="/march-madness">
                <div className="px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap flex items-center gap-2 text-slate-300 hover:bg-slate-800/50 hover:text-white">
                  <i className="ri-basketball-line w-4 h-4 flex items-center justify-center"></i>
                  March Madness
                </div>
              </a>
              <a href="/super-bowl-props">
                <div className="px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap flex items-center gap-2 text-slate-300 hover:bg-slate-800/50 hover:text-white">
                  <i className="ri-football-line w-4 h-4 flex items-center justify-center"></i>
                  Super Bowl
                </div>
              </a>

              {/* Markets Dropdown */}
              <div className="relative" ref={marketsDropdownRef} onMouseLeave={handleMarketsMouseLeave}>
                <button
                  onClick={() => setMarketsDropdownOpen(!marketsDropdownOpen)}
                  className="px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap flex items-center gap-2 text-slate-300 hover:bg-slate-800/50 hover:text-white"
                >
                  <i className="ri-bar-chart-2-line w-4 h-4 flex items-center justify-center"></i>
                  Markets
                  <i className={`ri-arrow-down-s-line text-xs transition-transform ${marketsDropdownOpen ? 'rotate-180' : ''}`}></i>
                </button>
                {marketsDropdownOpen && (
                  <div className="absolute top-full left-0 mt-2 w-52 rounded-xl border border-slate-700/80 shadow-2xl overflow-hidden z-50" style={{ background: 'rgba(30,30,30,0.98)' }}>
                    <div className="px-3 py-2 border-b border-slate-700/50">
                      <span className="text-xs text-slate-500 font-semibold uppercase tracking-wider">Market Types</span>
                    </div>
                    {[
                      { label: 'Spreads',      icon: 'ri-bar-chart-grouped-line', color: 'text-teal-400',    path: '/markets/spreads' },
                      { label: 'Money Lines',  icon: 'ri-copper-coin-line',       color: 'text-amber-400',   path: '/markets/money-lines' },
                      { label: 'Futures',      icon: 'ri-calendar-event-line',    color: 'text-emerald-400', path: '/markets/futures' },
                      { label: 'Parlays',      icon: 'ri-stack-line',             color: 'text-violet-400',  path: '/markets/parlays' },
                      { label: 'Player Props', icon: 'ri-user-star-line',         color: 'text-cyan-400',    path: '/markets/player-props' },
                    ].map(item => (
                      <a key={item.path} href={item.path} onClick={() => setMarketsDropdownOpen(false)}>
                        <div className="flex items-center gap-3 px-4 py-2.5 text-slate-300 hover:bg-slate-800/60 hover:text-white transition-colors cursor-pointer">
                          <i className={`${item.icon} ${item.color} text-base w-4 h-4 flex items-center justify-center`}></i>
                          <span className="text-sm font-medium">{item.label}</span>
                        </div>
                      </a>
                    ))}
                    <div className="border-t border-slate-700/50 px-3 py-2">
                      <a href="/markets" onClick={() => setMarketsDropdownOpen(false)}>
                        <div className="flex items-center justify-between text-teal-400 hover:text-teal-300 transition-colors cursor-pointer text-sm font-semibold py-1">
                          <span>All Markets</span>
                          <i className="ri-arrow-right-line"></i>
                        </div>
                      </a>
                    </div>
                  </div>
                )}
              </div>

              <a href="/why-rtj">
                <div className="px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap flex items-center gap-2 bg-teal-500/20 text-teal-400">
                  <i className="ri-question-line w-4 h-4 flex items-center justify-center"></i>
                  Why L2L
                </div>
              </a>
              <a href="/my-account">
                <div className="px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap flex items-center gap-2 text-slate-300 hover:bg-slate-800/50 hover:text-white">
                  <i className="ri-user-line w-4 h-4 flex items-center justify-center"></i>
                  My Account
                </div>
              </a>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <div className="hidden sm:flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-teal-500/20 to-teal-600/10 rounded-lg border border-teal-500/30">
                <i className="ri-wallet-3-line text-teal-400 text-sm"></i>
                <span className="font-semibold text-white text-sm whitespace-nowrap">$10,000</span>
              </div>
              <button className="relative p-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg border border-slate-700 transition-colors cursor-pointer">
                <i className="ri-file-list-3-line text-teal-500 text-lg w-5 h-5 flex items-center justify-center"></i>
              </button>
              <a href="/my-account">
                <div className="p-2 rounded-lg transition-colors cursor-pointer bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700">
                  <i className="ri-user-line text-lg w-5 h-5 flex items-center justify-center"></i>
                </div>
              </a>
              <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="lg:hidden p-2 text-slate-300 hover:text-white hover:bg-slate-800/50 rounded-lg transition-colors cursor-pointer">
                <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
              </button>
            </div>
          </div>
          {mobileMenuOpen && (
            <div className="lg:hidden border-t border-slate-700/50 py-3 space-y-1">
              {NAV_LINKS.map(link => (
                <a key={link.href} href={link.href}>
                  <div className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-slate-300 hover:bg-slate-800/50 hover:text-white transition-colors cursor-pointer">
                    <i className={`${link.icon} text-base`}></i>
                    <span className="text-sm font-medium">{link.label}</span>
                  </div>
                </a>
              ))}
            </div>
          )}
        </div>
      </nav>

      {/* Hero */}
      <PageHero
        badgeLabel="Why L2L"
        badgeColor="teal"
        title="Sports Trading, Reimagined"
        subtitle="L2L is a sports trading exchange where you buy and sell shares on game outcomes. Unlike traditional sportsbooks, you're trading against other users—not the house."
        backgroundImage="https://readdy.ai/api/search-image?query=Abstract%20modern%20sports%20trading%20concept%20with%20dynamic%20geometric%20shapes%2C%20financial%20charts%20merging%20with%20athletic%20motion%20blur%2C%20deep%20charcoal%20background%20with%20teal%20and%20emerald%20accent%20lighting%2C%20futuristic%20digital%20interface%20elements%2C%20professional%20business%20photography%20style%2C%20clean%20minimalist%20composition&width=1440&height=400&seq=why-rtj-hero-bg-001&orientation=landscape"
      />

      {/* Comparison Table */}
      <section className="py-12 px-4">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-white text-center mb-8">L2L vs Traditional Sportsbooks &amp; Other Prediction Markets</h2>
          <div className="rounded-2xl border border-slate-700/60 overflow-hidden shadow-2xl">
            <style>{`
              .comparison-scroll::-webkit-scrollbar {
                height: 10px;
              }
              .comparison-scroll::-webkit-scrollbar-track {
                background: #1e293b;
              }
              .comparison-scroll::-webkit-scrollbar-thumb {
                background: linear-gradient(90deg, #14b8a6, #10b981);
                border-radius: 8px;
                border: 2px solid #1e293b;
              }
              .comparison-scroll::-webkit-scrollbar-thumb:hover {
                background: linear-gradient(90deg, #0d9488, #059669);
              }
            `}</style>
            <div
              className="comparison-scroll overflow-x-auto"
              style={{ scrollbarWidth: 'thick', scrollbarColor: '#14b8a6 #1e293b' }}
            >
              <table className="w-full min-w-[580px]">
                <thead>
                  <tr className="border-b border-slate-700/60" style={{ background: 'rgba(30,30,30,0.95)' }}>
                    <th className="text-left px-6 py-4 text-slate-300 font-semibold text-sm w-1/3">Feature / Benefit</th>
                    <th className="px-6 py-4 text-center w-1/5">
                      <span className="text-teal-400 font-bold text-base">L2L</span>
                    </th>
                    <th className="px-6 py-4 text-center text-slate-400 font-semibold text-sm w-1/4">Other Prediction Markets</th>
                    <th className="px-6 py-4 text-center text-slate-400 font-semibold text-sm w-1/4">Typical Sportsbook</th>
                  </tr>
                </thead>
                <tbody>
                  {COMPARISON_ROWS.map((row, i) => (
                    <tr key={i} className={`border-b border-slate-700/40 transition-colors hover:bg-slate-800/20 ${i % 2 === 0 ? 'bg-slate-900/30' : 'bg-transparent'}`}>
                      <td className="px-6 py-4 text-slate-200 font-medium text-sm">{row.feature}</td>
                      <td className="px-6 py-4">
                        <div className="flex flex-col items-center gap-1">
                          {row.rtj === true ? <CheckIcon /> : row.rtj === false ? <CrossIcon note={typeof row.rtj === 'string' ? row.rtj : undefined} /> : <WarnIcon note={typeof row.rtj === 'string' ? row.rtj : undefined} />}
                          {row.rtjNote && <span className="text-xs text-teal-400 font-semibold">{row.rtjNote}</span>}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex justify-center">
                          {row.others === true ? <CheckIcon /> : row.others === false ? <CrossIcon note={row.othersNote} /> : <WarnIcon note={row.othersNote} />}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex justify-center">
                          {row.sportsbook === true ? <CheckIcon /> : row.sportsbook === false ? <CrossIcon /> : <WarnIcon note={row.sbNote} />}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* Feature Cards */}
      <section className="py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-white text-center mb-10">Why Traders Choose L2L</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((f, i) => (
              <div key={i} className="rounded-xl border border-slate-700/60 p-6 hover:border-slate-500/60 transition-all hover:shadow-xl" style={{ background: 'rgba(30,30,30,0.8)' }}>
                <div className={`w-12 h-12 ${f.bg} rounded-xl flex items-center justify-center mb-4`}>
                  <i className={`${f.icon} ${f.color} text-2xl`}></i>
                </div>
                <h3 className="text-white font-bold text-lg mb-2">{f.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-14 px-4" style={{ background: 'rgba(20,20,20,0.6)' }}>
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-white text-center mb-12">How It Works</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {STEPS.map((step, i) => (
              <div key={i} className="relative text-center">
                {i < STEPS.length - 1 && (
                  <div className="hidden lg:block absolute top-10 left-[calc(50%+2.5rem)] w-[calc(100%-5rem)] h-px bg-gradient-to-r from-teal-500/40 to-transparent"></div>
                )}
                <div className="relative inline-flex items-center justify-center w-20 h-20 rounded-full bg-teal-500/10 border border-teal-500/30 mb-4">
                  <i className={`${step.icon} text-teal-400 text-2xl`}></i>
                  <span className="absolute -top-2 -right-2 w-6 h-6 bg-teal-500 text-slate-900 text-xs font-bold rounded-full flex items-center justify-center">{step.num}</span>
                </div>
                <h3 className="text-white font-bold text-base mb-3">Step {step.num}<br />{step.label}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-14 px-4">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-white text-center mb-10">What Traders Say</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <div key={i} className="rounded-xl border border-slate-700/60 p-6 hover:border-slate-500/60 transition-all" style={{ background: 'rgba(30,30,30,0.8)' }}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0 border border-slate-600">
                    <img src={t.avatar} alt={t.name} className="w-full h-full object-cover object-top" />
                  </div>
                  <div>
                    <div className="text-white font-semibold text-sm">{t.name}</div>
                    <div className="text-slate-500 text-xs">{t.role}</div>
                  </div>
                </div>
                <div className="flex gap-0.5 mb-3">
                  {[...Array(5)].map((_, s) => (<i key={s} className="ri-star-fill text-amber-400 text-sm"></i>))}
                </div>
                <p className="text-slate-300 text-sm leading-relaxed italic">{t.quote}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <div className="rounded-2xl border border-teal-500/20 p-10" style={{ background: 'linear-gradient(135deg, rgba(20,184,166,0.08) 0%, rgba(30,30,30,0.95) 100%)' }}>
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">Ready to Trade Smarter?</h2>
            <p className="text-slate-400 mb-8 max-w-xl mx-auto">
              Join thousands of traders who have discovered a better way to bet on sports. Start with as little as $1 per share.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={() => navigate('/markets')}
                className="inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-gradient-to-r from-teal-400 to-emerald-400 hover:from-teal-500 hover:to-emerald-500 text-slate-900 font-bold rounded-lg shadow-lg shadow-teal-500/25 transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-rocket-line"></i>
                Start Trading Now
              </button>
              <button
                onClick={() => navigate('/my-account')}
                className="inline-flex items-center justify-center gap-2 px-8 py-3.5 border border-slate-600 text-slate-300 hover:bg-slate-800 hover:text-white rounded-lg transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-user-add-line"></i>
                Create Account
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-700/50 py-8 px-4" style={{ background: '#1e1e1e' }}>
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="h-10 w-40 flex items-center justify-center">
            <img
              src="https://static.readdy.ai/image/bc21bbd943df20a73e2c62e0eb37a79f/356d7ca1b6cdb5f780c70d0d0bd7c02d.png"
              alt="L2L Logo"
              className="h-full w-full object-contain"
            />
          </div>
          <p className="text-slate-600 text-xs text-center">Demo only. Not real money. For educational purposes only. Must be 18+ to participate.</p>
          <div className="flex items-center gap-4 text-slate-500 text-xs">
            <a href="/" className="hover:text-slate-300 transition-colors cursor-pointer">Home</a>
            <a href="/markets" className="hover:text-slate-300 transition-colors cursor-pointer">Markets</a>
            <a href="/super-bowl-props" className="hover:text-slate-300 transition-colors cursor-pointer">Super Bowl</a>
            <a href="/march-madness" className="hover:text-slate-300 transition-colors cursor-pointer">March Madness</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
